import {
  ImportantContactType,
  type ResidentBody,
  type ResidentItem
} from '@lmc/ts-common'
import type { ImportantContactBodyDtos } from '@lmc/ts-common/lib/dtos/Resident'
import {
  Checkbox,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Select
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { UpdateResidentReturnType } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BaseContactForm, { type BaseFormType } from './BaseContactForm'
import getOptionsWithOneMoreValue from './utils/getOptionsWithOneMoreValue'
import { relationshipOptions } from './utils/relationshipOptions'

export { ContactStatus } from '@lmc/ts-common'

export type FormType = BaseFormType &
  Pick<ImportantContactBodyDtos, 'lpa' | 'observations' | 'contactableTimes'>

type PersonalContactFormProps = {
  onSubmit: (values: Partial<ResidentBody>) => Promise<UpdateResidentReturnType>
  resident: ResidentItem | undefined
  isLoading: boolean
  editedIndex: number | undefined
  testId?: string
}

const PersonalContactForm = ({
  onSubmit,
  resident,
  isLoading,
  editedIndex,
  testId
}: PersonalContactFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const getNewContact = (values: FormType): ImportantContactBodyDtos => {
    const newContact: ImportantContactBodyDtos = {
      ...values,
      type: ImportantContactType.personal,
      name: {
        first: values.firstName,
        last: values.lastName
      }
    }

    return newContact
  }

  const residentName =
    resident?.name?.first &&
    resident?.name.last &&
    `${resident.name.first} ${resident.name.last}'s`

  const editedContact =
    editedIndex !== undefined
      ? resident?.importantContacts[editedIndex]
      : undefined

  const options = useMemo(
    () =>
      getOptionsWithOneMoreValue(
        relationshipOptions,
        editedContact?.relationship
      ),
    [editedContact?.relationship]
  )
  const sortedOptions = options.sort((a, b) => (a.label > b.label ? 1 : -1))

  return (
    <BaseContactForm<FormType>
      onSubmit={onSubmit}
      resident={resident}
      editedIndex={editedIndex}
      getNewContact={getNewContact}
      isLoading={isLoading}
      title={`${residentName} ${t(supConstants.PERSONAL_CONTACT)}`}
      testId={testId}
    >
      <FormItem label={t(supConstants.RELATIONSHIP_ROLE)} name="relationship">
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          showSearch
          optionFilterProp="label"
          options={sortedOptions}
        />
      </FormItem>
      <FormItem label={t(supConstants.NEXT_OF_KIN)} name="nextOfKin">
        <FormRadioGroup>
          <FormRadio value text={t(supConstants.YES)} />
          <FormRadio value={false} text={t(supConstants.NO)} />
        </FormRadioGroup>
      </FormItem>
      <FormItem
        label={t(supConstants.UNPAID_CARER)}
        name="unpaidCarer"
        valuePropName="checked"
      >
        <Checkbox />
      </FormItem>
    </BaseContactForm>
  )
}

export default PersonalContactForm
